import React, { useId, useState } from 'react';
import Button from '../ui/Button';
import { errorMessageOf } from '../../utils/apiErrors';
import type { PersonInput } from '../../types/People';

interface AddPersonFormProps {
  onSave: (input: PersonInput) => Promise<void>;
  onCancel: () => void;
}

const EMPTY: PersonInput = { name: '', email: '', phone: '', whatsapp: '', notes: '' };

/** Adds a contact to the workspace. Only the name is required. */
const AddPersonForm: React.FC<AddPersonFormProps> = ({ onSave, onCancel }) => {
  const uid = useId();
  const [draft, setDraft] = useState<PersonInput>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof PersonInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setDraft(prev => ({ ...prev, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      onCancel();
    } catch (err) {
      setError(errorMessageOf(err, "Couldn't save this person. Please try again."));
      setSaving(false);
    }
  };

  return (
    <form className="settings-card people-form" onSubmit={submit} noValidate>
      <h4 className="settings-card-title">Add person</h4>
      <p className="settings-muted-xs">A contact only. They cannot sign in until you give them a login.</p>
      <div className="people-form__fields">
        <div className="form-group">
          <label htmlFor={`${uid}-name`}>Name <span className="form-required">*</span></label>
          <input id={`${uid}-name`} type="text" value={draft.name} onChange={set('name')} autoFocus />
        </div>
        <div className="form-group">
          <label htmlFor={`${uid}-email`}>Email</label>
          <input id={`${uid}-email`} type="email" value={draft.email ?? ''} onChange={set('email')} />
        </div>
        <div className="form-group">
          <label htmlFor={`${uid}-phone`}>Phone</label>
          <input id={`${uid}-phone`} type="tel" value={draft.phone ?? ''} onChange={set('phone')} />
        </div>
        <div className="form-group">
          <label htmlFor={`${uid}-wa`}>WhatsApp</label>
          <input id={`${uid}-wa`} type="tel" value={draft.whatsapp ?? ''} onChange={set('whatsapp')} />
        </div>
        <div className="form-group people-form__wide">
          <label htmlFor={`${uid}-notes`}>Notes</label>
          <textarea id={`${uid}-notes`} rows={2} value={draft.notes ?? ''} onChange={set('notes')} />
        </div>
      </div>
      {error && <p className="form-error-text" role="alert">{error}</p>}
      <div className="people-form__actions">
        <Button type="submit" variant="primary" loading={saving} disabled={!draft.name.trim()}>Save person</Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </form>
  );
};

export default AddPersonForm;
